import { useRef, useEffect, KeyboardEvent } from 'react';
import {
  useChatStore,
  HISTORY_FAIL_MESSAGE,
  WELCOME_MESSAGE,
} from '../store/chatStore';
import { useSendMessage } from '../hooks/useSendMessage';
import { MessageBubble } from '../components/MessageBubble';
import { useAuthStore } from '../store/authStore';
import { fetchChatHistory } from '../services/chatService';
import styles from './ChatPage.module.scss';

export function ChatPage() {
  const {
    messages,
    isLoading,
    isHistoryLoading,
    setMessages,
    setHistoryLoading,
    addMessage,
  } = useChatStore();
  const { aiToken, sessionId, auth } = useAuthStore();
  const { inputValue, setInputValue, sendMessage } = useSendMessage();

  useEffect(() => {
    if (!sessionId || !aiToken || !auth) return;

    fetchChatHistory(aiToken, sessionId, auth)
      .then((history) => {
        if (history.length === 0) {
          addMessage('ai', WELCOME_MESSAGE);
        } else {
          setMessages(history);
        }
      })
      .catch(() => setMessages([HISTORY_FAIL_MESSAGE]))
      .finally(() => setHistoryLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load history once per session; a token refresh must not refetch it
  }, [sessionId]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.chatWrapper}>
        <div className={styles.messages}>
          {messages.map((msg) => (
            <MessageBubble key={msg.id} message={msg} />
          ))}

          {(isHistoryLoading || isLoading) && (
            <div className={styles.aiGroup}>
              <span className={styles.aiLabel}>Paymob AI</span>
              <div className={styles.typingBubble}>
                <span />
                <span />
                <span />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className={styles.inputArea}>
          <textarea
            ref={textareaRef}
            className={styles.input}
            placeholder="Type your message..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
          />
          <button
            className={styles.sendButton}
            onClick={sendMessage}
            disabled={!inputValue.trim() || isLoading || isHistoryLoading}
            aria-label="Send message"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 12H19M19 12L12 5M19 12L12 19"
                stroke="white"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
